import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div role="alert" className="sgs-error-state">
        <div className="sgs-error-state__icon" aria-hidden>
          <AlertTriangle size={20} strokeWidth={1.75} />
        </div>
        <div className="sgs-error-state__body">
          <h2 className="sgs-error-state__title">Subsystem fault</h2>
          <p className="sgs-error-state__desc">
            An unhandled error stopped this view from rendering. Reset the view, or reload the page if it persists.
          </p>
          <pre className="sgs-error-state__detail" aria-label="Error detail">
            {error.message}
          </pre>
          <button type="button" className="sgs-btn sgs-btn--secondary" onClick={this.reset}>
            <RotateCcw size={14} strokeWidth={2} aria-hidden /> Reset view
          </button>
        </div>
      </div>
    );
  }
}
